import React, { useCallback, useRef } from 'react';
import { Autocomplete, useLoadScript } from '@react-google-maps/api';
import { Input, Alert } from 'antd';
import type { InputProps } from 'antd';
import type { ParsedGooglePlace } from '../utils/addressUtils';
import { parseGooglePlace } from '../utils/addressUtils';

const libraries: 'places'[] = ['places'];

export interface AddressAutocompleteInputProps extends Omit<InputProps, 'onChange'> {
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onPlaceResolved?: (place: ParsedGooglePlace) => void;
}

/** Google Places Autocomplete using Ant Design Input for consistent form styling. */
const LegacyAutocompleteInput: React.FC<AddressAutocompleteInputProps> = ({
  onPlaceResolved,
  onChange,
  value,
  ...inputProps
}) => {
  const acRef = useRef<any>(null);

  const onLoad = useCallback((autocomplete: any) => {
    acRef.current = autocomplete;
    autocomplete.setFields(['address_components', 'formatted_address', 'geometry', 'name']);
  }, []);

  const onPlaceChanged = useCallback(() => {
    const place = acRef.current?.getPlace();
    if (!place || !place.address_components?.length) return;
    const parsed = parseGooglePlace(place);
    onPlaceResolved?.(parsed);
    onChange?.({ target: { value: parsed.streetLine } } as React.ChangeEvent<HTMLInputElement>);
  }, [onChange, onPlaceResolved]);

  return (
    <Autocomplete onLoad={onLoad} onUnmount={() => (acRef.current = null)} onPlaceChanged={onPlaceChanged} options={{ types: ['address'] }}>
      <Input {...inputProps} value={value} onChange={onChange} />
    </Autocomplete>
  );
};

const AddressAutocompleteInput: React.FC<AddressAutocompleteInputProps> = (props) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    return <Input {...props} />;
  }
  return <AddressAutocompleteWithScript {...props} />;
};

const AddressAutocompleteWithScript: React.FC<AddressAutocompleteInputProps> = (props) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY!;
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
    version: 'weekly',
  });

  if (loadError) {
    return (
      <div style={{ width: '100%' }}>
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 8 }}
          message="Address search unavailable"
          description="Google Maps script failed to load (check API key, Places API, billing, and HTTP referrer restrictions). You can still type the address manually."
        />
        <Input {...props} placeholder={props.placeholder || 'Enter street address'} />
      </div>
    );
  }
  if (!isLoaded) {
    return <Input {...props} disabled placeholder="Loading address search…" />;
  }
  return <LegacyAutocompleteInput {...props} />;
};

export default AddressAutocompleteInput;
